import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { BroadcastModal, Broadcast } from '../components/BroadcastModal';
import BroadcastLoadingState from '../components/BroadcastModal/BroadcastLoadingState';
import BroadcastBlockedIndicator from '../components/BroadcastModal/BroadcastBlockedIndicator';
import wsService from '../services/websocket';

interface BroadcastContextType {
  // Добавить broadcast в очередь (для low/normal priority - в колокольчик)
  addBroadcast: (broadcast: Broadcast) => void;
  // Количество непрочитанных обычных broadcast (для колокольчика)
  unreadCount: number;
  // Список обычных уведомлений для колокольчика
  notifications: Broadcast[];
  // Отметить как прочитанное
  markAsRead: (id: number) => void;
}

const BroadcastContext = createContext<BroadcastContextType | null>(null);

interface BroadcastProviderProps {
  children: ReactNode;
  socket?: any; // Deprecated - используем wsService напрямую
}

export const BroadcastProvider: React.FC<BroadcastProviderProps> = ({ children }) => {
  // Очередь high-priority broadcasts (показываются модальными окнами)
  const [modalQueue, setModalQueue] = useState<Broadcast[]>([]);
  // Текущий показываемый broadcast
  const [currentModal, setCurrentModal] = useState<Broadcast | null>(null);
  // Обычные уведомления (для колокольчика)
  const [notifications, setNotifications] = useState<Broadcast[]>([]);
  // Состояние загрузки (false по умолчанию - не показываем если нет уведомлений)
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Загрузка непрочитанных broadcasts при монтировании
  useEffect(() => {
    const loadPendingBroadcasts = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return; // Нет авторизации - пропускаем

        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };

        // Загружаем high-priority (модальные окна)
        const highPriorityResponse = await fetch('/api/broadcasts/pending', {
          headers,
          credentials: 'include'
        });
        if (!highPriorityResponse.ok) throw new Error(`HTTP ${highPriorityResponse.status}`);
        const highPriorityData = await highPriorityResponse.json();

        if (highPriorityData.success && highPriorityData.data?.length > 0) {
          setPendingCount(highPriorityData.data.length);
          setModalQueue(highPriorityData.data);
        }

        // Загружаем normal/low priority (колокольчик)
        const normalPriorityResponse = await fetch('/api/broadcasts/pending-normal', {
          headers,
          credentials: 'include'
        });
        if (!normalPriorityResponse.ok) throw new Error(`HTTP ${normalPriorityResponse.status}`);
        const normalPriorityData = await normalPriorityResponse.json();

        if (normalPriorityData.success && normalPriorityData.data?.length > 0) {
          console.log('🔔 Загружено уведомлений для колокольчика:', normalPriorityData.data.length);
          setNotifications(normalPriorityData.data.map((b: any) => ({
            id: b.id,
            title: b.title,
            message: b.message,
            priority: b.priority,
            buttonText: b.button_text,
            buttonUrl: b.button_url
          })));
        }
      } catch (error) {
        console.error('Error loading pending broadcasts:', error);
      }
    };

    loadPendingBroadcasts();
  }, []);

  // Подписка на WebSocket события через wsService
  useEffect(() => {
    const handleNewBroadcast = (broadcast: Broadcast) => {
      console.log('BroadcastContext: New broadcast received:', broadcast);

      if (broadcast.priority === 'high') {
        // High priority - добавляем в очередь модальных окон
        setModalQueue(prev => [...prev, broadcast]);
      } else {
        // Normal/Low priority - добавляем в уведомления (колокольчик)
        setNotifications(prev => [broadcast, ...prev]);
      }
    };

    // Подписка через wsService.on (локальные слушатели)
    const unsubscribeBroadcast = wsService.on('broadcast:new', handleNewBroadcast);

    // Подписка на комнату уведомлений после подключения
    const handleConnected = (connected: boolean) => {
      if (connected) {
        const socket = wsService.getSocket();
        if (socket) {
          // Даём время на auth
          setTimeout(() => {
            socket.emit('notifications:subscribe');
            console.log('BroadcastContext: subscribed to notifications via wsService');
          }, 1000);
        }
      }
    };

    // Подписываемся на событие подключения
    const unsubscribeConnected = wsService.on('connected', handleConnected);

    // Если уже подключены - подписываемся сразу
    if (wsService.isConnected()) {
      handleConnected(true);
    }

    // Также подписываемся напрямую на socket если он есть
    const socket = wsService.getSocket();
    if (socket) {
      socket.on('broadcast:new', handleNewBroadcast);
    }

    return () => {
      unsubscribeBroadcast();
      unsubscribeConnected();
      const socket = wsService.getSocket();
      if (socket) {
        socket.off('broadcast:new', handleNewBroadcast);
      }
    };
  }, []);

  // Показываем следующий модальный broadcast из очереди
  useEffect(() => {
    if (!currentModal && modalQueue.length > 0) {
      setCurrentModal(modalQueue[0]);
      setModalQueue(prev => prev.slice(1));
    }
  }, [currentModal, modalQueue]);

  // Добавляем blur на .app когда показана модалка
  // Модалка через Portal вне .app, поэтому она не размывается
  useEffect(() => {
    const appElement = document.querySelector('.app');
    if (currentModal || isLoadingPending) {
      appElement?.classList.add('broadcast-blur-active');
    } else {
      appElement?.classList.remove('broadcast-blur-active');
    }
    return () => {
      appElement?.classList.remove('broadcast-blur-active');
    };
  }, [currentModal, isLoadingPending]);

  // Закрытие модального окна
  const handleCloseModal = useCallback(() => {
    setCurrentModal(null);
  }, []);

  // Добавить broadcast (вызывается из других компонентов)
  const addBroadcast = useCallback((broadcast: Broadcast) => {
    if (broadcast.priority === 'high') {
      setModalQueue(prev => [...prev, broadcast]);
    } else {
      setNotifications(prev => [broadcast, ...prev]);
    }
  }, []);

  // Отметить как прочитанное (для колокольчика)
  const markAsRead = useCallback(async (id: number) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/broadcasts/${id}/read`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Error marking broadcast as read:', error);
    }
  }, []);

  const value: BroadcastContextType = {
    addBroadcast,
    unreadCount: notifications.length,
    notifications,
    markAsRead
  };

  return (
    <BroadcastContext.Provider value={value}>
      {children}

      {/* Индикатор загрузки */}
      {isLoadingPending && (
        <BroadcastLoadingState count={pendingCount} />
      )}

      {/* Индикатор блокировки - показывается когда есть модал */}
      {currentModal && (
        <>
          <BroadcastBlockedIndicator />
          <BroadcastModal
            broadcast={currentModal}
            onClose={handleCloseModal}
            current={pendingCount - modalQueue.length}
            total={pendingCount}
          />
        </>
      )}
    </BroadcastContext.Provider>
  );
};

export const useBroadcast = (): BroadcastContextType => {
  const context = useContext(BroadcastContext);
  if (!context) {
    throw new Error('useBroadcast must be used within a BroadcastProvider');
  }
  return context;
};

export default BroadcastContext;
